import { apiRequest, ApiError, getApiBaseUrl } from '@/lib/api/client';
import { getAuthToken } from '@/lib/api/token-store';

function createAuthHeaders(token, headers = {}) {
  const normalizedToken = String(token || getAuthToken() || '').trim();
  if (!normalizedToken) {
    throw new ApiError('authorization token is required', 401);
  }
  return {
    Authorization: `Bearer ${normalizedToken}`,
    ...headers,
  };
}

/**
 * GET /api/archived-messages/by-phone/{phone}
 *
 * @param {{ phone: string, limit?: number, skip?: number, token?: string }} params
 * @returns {Promise<{ count: number, limit: number, skip: number, messages: Array }>}
 */
export async function fetchArchivedMessagesByPhone({ phone, limit = 5, skip = 0, token } = {}) {
  const normalizedPhone = String(phone || '').trim().replaceAll('+', '');
  if (!normalizedPhone) {
    throw new ApiError('phone is required', 400);
  }

  const query = new URLSearchParams({
    limit: String(Number(limit) || 20),
    skip: String(Number(skip) || 0),
  });

  const payload = await apiRequest(
    `/api/archived-messages/by-phone/${encodeURIComponent(normalizedPhone)}?${query.toString()}`,
    {
      method: 'GET',
      headers: createAuthHeaders(token),
    },
  );

  return {
    count: Number(payload?.count) || 0,
    limit: Number(payload?.limit) || limit,
    skip: Number(payload?.skip) || skip,
    messages: Array.isArray(payload?.messages) ? payload.messages : [],
  };
}

/**
 * POST /api/archived-messages:search
 *
 * At least one of fromDate, toDate, phrases, or phone must be provided.
 *
 * @param {{
 *   fromDate?: string,
 *   toDate?: string,
 *   phrases?: string[],
 *   phone?: string,
 *   limit?: number,
 *   skip?: number,
 *   token?: string,
 * }} params
 * @returns {Promise<{ count: number, limit: number, skip: number, messages: Array }>}
 */
export async function searchArchivedMessages({
  fromDate,
  toDate,
  phrases,
  phone,
  limit = 20,
  skip = 0,
  token,
} = {}) {
  const hasFromDate = typeof fromDate === 'string' && fromDate.trim();
  const hasToDate = typeof toDate === 'string' && toDate.trim();
  const hasPhrases = Array.isArray(phrases) && phrases.filter((p) => String(p || '').trim()).length > 0;
  const hasPhone = typeof phone === 'string' && phone.trim();

  if (!hasFromDate && !hasToDate && !hasPhrases && !hasPhone) {
    throw new ApiError('at least one search filter is required (fromDate, toDate, phrases, or phone)', 400);
  }

  const body = {
    limit: Number(limit) || 20,
    skip: Number(skip) || 0,
  };

  if (hasFromDate) body.fromDate = fromDate.trim();
  if (hasToDate) body.toDate = toDate.trim();
  if (hasPhrases) body.phrases = phrases.map((p) => String(p || '').trim()).filter(Boolean);
  if (hasPhone) body.phone = phone.trim();

  const payload = await apiRequest('/api/archived-messages/search', {
    method: 'POST',
    headers: createAuthHeaders(token, { 'Content-Type': 'application/json' }),
    body: JSON.stringify(body),
  });

  return {
    count: Number(payload?.count) || 0,
    limit: Number(payload?.limit) || body.limit,
    skip: Number(payload?.skip) || body.skip,
    messages: Array.isArray(payload?.messages) ? payload.messages : [],
  };
}

/**
 * GET /api/archived-messages/by-phone/{phone}/around/{messageId}
 *
 * Returns a window of messages around the given message (older + anchor + newer).
 *
 * @param {{ phone: string, messageId: string|number, token?: string }} params
 * @returns {Promise<{ count: number, limit: number, skip: number, messages: Array, before: number, after: number }>}
 */
export async function fetchArchivedMessagesAround({ phone, messageId, token } = {}) {
  const normalizedPhone = String(phone || '').trim().replaceAll('+', '');
  if (!normalizedPhone) {
    throw new ApiError('phone is required', 400);
  }
  if (messageId === undefined || messageId === null || messageId === '') {
    throw new ApiError('messageId is required', 400);
  }

  const payload = await apiRequest(
    `/api/archived-messages/by-phone/${encodeURIComponent(normalizedPhone)}/around/${encodeURIComponent(messageId)}`,
    {
      method: 'GET',
      headers: createAuthHeaders(token),
    },
  );

  return {
    count: Number(payload?.count) || 0,
    limit: Number(payload?.limit) || 0,
    skip: Number(payload?.skip) || 0,
    messages: Array.isArray(payload?.messages) ? payload.messages : [],
    before: Number(payload?.before) || 0,
    after: Number(payload?.after) || 0,
  };
}

/**
 * GET /api/archived-messages/by-phone/{phone}/before/{messageId}
 *
 * Returns messages older than the given message ID (for infinite scroll upward).
 *
 * @param {{ phone: string, messageId: string|number, token?: string }} params
 * @returns {Promise<{ count: number, limit: number, messages: Array }>}
 */
export async function fetchArchivedMessagesBefore({ phone, messageId, token } = {}) {
  const normalizedPhone = String(phone || '').trim().replaceAll('+', '');
  if (!normalizedPhone) {
    throw new ApiError('phone is required', 400);
  }
  if (messageId === undefined || messageId === null || messageId === '') {
    throw new ApiError('messageId is required', 400);
  }

  const payload = await apiRequest(
    `/api/archived-messages/by-phone/${encodeURIComponent(normalizedPhone)}/before/${encodeURIComponent(messageId)}`,
    {
      method: 'GET',
      headers: createAuthHeaders(token),
    },
  );

  return {
    count: Number(payload?.count) || 0,
    limit: Number(payload?.limit) || 0,
    messages: Array.isArray(payload?.messages) ? payload.messages : [],
  };
}

/**
 * GET /api/archived-messages/by-phone/{phone}/after/{messageId}
 *
 * Returns messages newer than the given message ID (for infinite scroll downward).
 *
 * @param {{ phone: string, messageId: string|number, token?: string }} params
 * @returns {Promise<{ count: number, limit: number, messages: Array }>}
 */
export async function fetchArchivedMessagesAfter({ phone, messageId, token } = {}) {
  const normalizedPhone = String(phone || '').trim().replaceAll('+', '');
  if (!normalizedPhone) {
    throw new ApiError('phone is required', 400);
  }
  if (messageId === undefined || messageId === null || messageId === '') {
    throw new ApiError('messageId is required', 400);
  }

  const payload = await apiRequest(
    `/api/archived-messages/by-phone/${encodeURIComponent(normalizedPhone)}/after/${encodeURIComponent(messageId)}`,
    {
      method: 'GET',
      headers: createAuthHeaders(token),
    },
  );

  return {
    count: Number(payload?.count) || 0,
    limit: Number(payload?.limit) || 0,
    messages: Array.isArray(payload?.messages) ? payload.messages : [],
  };
}

/**
 * Downloads an archived media file using the media_url path from the message.
 * The media_url is used verbatim as the path after the API base URL.
 *
 * @param {{ mediaUrl: string, fileName?: string, token?: string }} params
 * @returns {Promise<void>}
 */
export async function downloadArchivedMedia({ mediaUrl, fileName, token } = {}) {
  if (!mediaUrl) throw new ApiError('mediaUrl is required', 400);

  const normalizedToken = String(token || getAuthToken() || '').trim();
  if (!normalizedToken) throw new ApiError('authorization token is required', 401);

  // mediaUrl already starts with /api/... so prepend base URL only
  const url = `${getApiBaseUrl()}${mediaUrl.startsWith('/') ? '' : '/'}${mediaUrl}`;

  const response = await fetch(url, {
    method: 'GET',
    headers: { Authorization: `Bearer ${normalizedToken}` },
  });

  if (!response.ok) {
    throw new ApiError(`Failed to download media (${response.status})`, response.status);
  }

  const blob = await response.blob();
  const objectUrl = URL.createObjectURL(blob);

  // Derive a filename from the URL path if not provided
  const derivedName = fileName || decodeURIComponent(mediaUrl.split('/').pop()) || 'download';

  const link = document.createElement('a');
  link.href = objectUrl;
  link.download = derivedName;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(objectUrl);
}
